import styles from './KnowledgeSourceTable.module.css'
import ContentSetList from './ContentSetList'
import ProductChip from './ProductChip'
import Icon from './Icon'
import { useIsMobile } from '../hooks/useIsMobile'
import {
  statusMetaFor,
  statusSummary,
  deleteAffordance,
  productVersionLabel,
  productVersionTitle,
  sourceTypePalette,
  DocumentGlyph,
  WebGlyph,
  truncate,
} from './sourceVisuals'
import { formatDate } from '../utils/formatDate'

// Sortable columns, in header order.
const COLUMNS = [
  { key: 'name',      label: 'Source',   colClass: 'sourceCol' },
  { key: 'products',  label: 'Products', colClass: 'productsCol' },
  { key: 'versions',  label: 'Versions', colClass: 'versionsCol' },
  { key: 'status',    label: 'Status',   colClass: 'statusCol' },
  { key: 'updatedAt', label: 'Updated',  colClass: 'updatedCol' },
]

// Products beyond this many collapse into a "+N" badge, so a document linked to
// a long list doesn't make its row taller than its neighbors.
const MAX_VISIBLE_PRODUCTS = 2

// Stands in for an empty cell. Spelled out rather than drawn as a dash, because
// the Versions column shows a bare "-" for content that applies to every product
// version — "nothing here" and "covers everything" shouldn't look alike.
const EMPTY_CELL = 'None'

function SortArrow({ direction }) {
  return (
    <Icon
      name="chevron-up"
      className={styles.sortArrow}
      style={{ transform: direction === 'desc' ? 'rotate(180deg)' : undefined }}
    />
  )
}

function Chevron({ expanded }) {
  return (
    <Icon
      name="chevron-right"
      className={styles.chevron}
      style={{ transform: expanded ? 'rotate(90deg)' : undefined }}
    />
  )
}

/**
 * A document's subtitle. Page counts are per content-set (and differ between
 * versions), so they belong in the expanded rows rather than here — and so
 * can file type, now that a document can hold different file types across
 * product versions (one uploaded as a DOCX, another replaced with a PDF).
 * This only speaks for the row when every content-set agrees; otherwise it
 * defers to "Document" and the expanded sub-row breaks it out per version.
 */
function sourceSubtitle(source) {
  if (source.type === 'web') return source.url
  const contentSets = source.contentSets ?? []
  const fileTypes = new Set(contentSets.map(cs => cs.fileType).filter(Boolean))
  return fileTypes.size === 1 ? [...fileTypes][0] : 'Document'
}

/**
 * @typedef {{id: number, name: string, type: string, url: string, products: string[], contentSets: Array<object>, updatedAt: string}} KnowledgeSource
 */

/**
 * KnowledgeSourceTable — the Knowledge Base source list. One row per document,
 * expandable to show that document's per-product-version content-sets.
 *
 * @param {object} props
 * @param {Array<KnowledgeSource>} [props.sources]
 * @param {number[]} [props.expandedIds] — ids whose content-sets are showing
 * @param {function(number): void} [props.onToggleExpand]
 * @param {function(KnowledgeSource): void} [props.onDelete] — delete the whole document
 * @param {function(KnowledgeSource, object): void} [props.onDeleteVersion] — delete one content-set
 * @param {function(KnowledgeSource): void} [props.onRowClick] — view/edit a source's details
 * @param {string} [props.sortKey]
 * @param {'asc'|'desc'} [props.sortDir]
 * @param {function(string): void} [props.onSort]
 */
export default function KnowledgeSourceTable({
  sources = [],
  expandedIds = [],
  onToggleExpand,
  onDelete,
  onDeleteVersion,
  onRowClick,
  sortKey = '',
  sortDir = 'asc',
  onSort,
}) {
  const isMobile = useIsMobile()

  if (!sources.length) {
    return <div className={styles.empty}>No sources match your filters.</div>
  }

  if (isMobile) {
    return (
      <div className={styles.cardList}>
        {sources.map(source => {
          const contentSets = source.contentSets ?? []
          const products = source.products ?? []
          const summary = statusSummary(contentSets)
          const palette = sourceTypePalette(source.type)
          const expandable = contentSets.length > 1
          const expanded = expandable && expandedIds.includes(source.id)
          const { blocked, retry } = deleteAffordance(contentSets)

          return (
            <div className={styles.sourceCard} key={source.id}>
              <div className={styles.sourceCardHeader} onClick={() => onRowClick?.(source)}>
                <button
                  type="button"
                  className={styles.expandBtn}
                  aria-expanded={expanded}
                  aria-label={`${expanded ? 'Hide' : 'Show'} versions of ${source.name}`}
                  disabled={!expandable}
                  onClick={e => { e.stopPropagation(); onToggleExpand?.(source.id) }}
                >
                  {expandable && <Chevron expanded={expanded} />}
                </button>
                <span
                  className={styles.sourceIcon}
                  style={{ color: palette.text, borderColor: palette.border, backgroundColor: palette.bg }}
                >
                  {source.type === 'web' ? <WebGlyph /> : <DocumentGlyph />}
                </span>
                <span className={styles.sourceText}>
                  <span className={styles.sourceName}>{source.name}</span>
                  <span className={styles.sourceMeta}>{sourceSubtitle(source)}</span>
                </span>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  aria-label={`${retry ? 'Retry deleting' : 'Delete'} ${source.name}`}
                  title={blocked
                    ? "Can't delete a source while one of its versions is still being processed"
                    : retry
                      ? "Deletion of this source hasn't finished — retry it"
                      : 'Delete this source and every version of it'}
                  disabled={blocked}
                  onClick={e => { e.stopPropagation(); onDelete?.(source) }}
                >
                  <Icon name="trash" />
                </button>
              </div>

              <div className={styles.mobileFields} onClick={() => onRowClick?.(source)}>
                <div className={styles.mobileField}>
                  <span className={styles.mobileFieldLabel}>Products</span>
                  {products.length === 0 ? (
                    <span className={styles.muted}>{EMPTY_CELL}</span>
                  ) : (
                    <span className={styles.productsList}>
                      {products.slice(0, MAX_VISIBLE_PRODUCTS).map(name => (
                        <ProductChip key={name} name={name} />
                      ))}
                      {products.length > MAX_VISIBLE_PRODUCTS && (
                        <span className={styles.overflowBadge}>
                          +{products.length - MAX_VISIBLE_PRODUCTS}
                        </span>
                      )}
                    </span>
                  )}
                </div>

                <div className={styles.mobileField}>
                  <span className={styles.mobileFieldLabel}>Versions</span>
                  {contentSets.length === 0 && <span className={styles.muted}>{EMPTY_CELL}</span>}
                  {contentSets.length === 1 && (
                    <span className={styles.versions}>{productVersionLabel(contentSets[0].productVersion)}</span>
                  )}
                  {contentSets.length > 1 && (
                    <span className={styles.versions}>{contentSets.length} versions</span>
                  )}
                </div>

                <div className={styles.mobileField}>
                  <span className={styles.mobileFieldLabel}>Status</span>
                  {summary === null ? (
                    <span className={styles.muted}>{EMPTY_CELL}</span>
                  ) : (
                    <span className={`${styles.statusPill} ${styles[statusMetaFor(summary.status).className]}`}>
                      {statusMetaFor(summary.status).label}
                      {summary.count < summary.total && (
                        <span className={styles.statusFraction}>
                          {summary.count}/{summary.total}
                        </span>
                      )}
                    </span>
                  )}
                </div>

                <div className={styles.mobileField}>
                  <span className={styles.mobileFieldLabel}>Updated</span>
                  <span className={styles.updated}>{formatDate(source.updatedAt)}</span>
                </div>
              </div>

              {expanded && (
                <div className={styles.mobileSubRow}>
                  <ContentSetList
                    contentSets={contentSets}
                    onDelete={cs => onDeleteVersion?.(source, cs)}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className={styles.wrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {COLUMNS.map(col => (
              <th key={col.key} className={styles[col.colClass]}>
                <button
                  type="button"
                  className={`${styles.sortBtn} ${sortKey === col.key ? styles.sortBtnActive : ''}`}
                  onClick={() => onSort?.(col.key)}
                  aria-sort={sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {col.label}
                  {sortKey === col.key && <SortArrow direction={sortDir} />}
                </button>
              </th>
            ))}
            <th className={styles.actionsCol} />
          </tr>
        </thead>
        <tbody>
          {sources.map(source => {
            const contentSets = source.contentSets ?? []
            const products = source.products ?? []
            const summary = statusSummary(contentSets)
            const palette = sourceTypePalette(source.type)
            // Only a document holding several versions has anything to expand
            // into: with one, the Versions and Status columns already report
            // everything the sub-row would.
            const expandable = contentSets.length > 1
            const expanded = expandable && expandedIds.includes(source.id)
            // A document is deletable unless one of its content-sets is
            // mid-ingest — the backend rejects the aggregate delete then. One
            // already marked Deleting doesn't block; that case becomes the retry.
            const { blocked, retry } = deleteAffordance(contentSets)

            return [
              <tr
                key={source.id}
                className={styles.row}
                onClick={() => onRowClick?.(source)}
              >
                <td className={styles.sourceCell}>
                  <button
                    type="button"
                    className={styles.expandBtn}
                    aria-expanded={expanded}
                    aria-label={`${expanded ? 'Hide' : 'Show'} versions of ${source.name}`}
                    disabled={!expandable}
                    onClick={e => { e.stopPropagation(); onToggleExpand?.(source.id) }}
                  >
                    {expandable && <Chevron expanded={expanded} />}
                  </button>
                  <span
                    className={styles.sourceIcon}
                    style={{ color: palette.text, borderColor: palette.border, backgroundColor: palette.bg }}
                  >
                    {source.type === 'web' ? <WebGlyph /> : <DocumentGlyph />}
                  </span>
                  <span className={styles.sourceText}>
                    <span className={styles.sourceName}>{source.name}</span>
                    <span className={styles.sourceMeta} title={sourceSubtitle(source)}>
                      {truncate(sourceSubtitle(source))}
                    </span>
                  </span>
                </td>

                <td className={styles.productsCol}>
                  {products.length === 0 ? (
                    <span className={styles.muted}>{EMPTY_CELL}</span>
                  ) : (
                    <span className={styles.productsList}>
                      {products.slice(0, MAX_VISIBLE_PRODUCTS).map(name => (
                        <ProductChip key={name} name={name} />
                      ))}
                      {products.length > MAX_VISIBLE_PRODUCTS && (
                        <span
                          className={styles.overflowBadge}
                          title={products.slice(MAX_VISIBLE_PRODUCTS).join(', ')}
                        >
                          +{products.length - MAX_VISIBLE_PRODUCTS}
                        </span>
                      )}
                    </span>
                  )}
                </td>

                <td className={styles.versions}>
                  {contentSets.length === 0 && <span className={styles.muted}>{EMPTY_CELL}</span>}
                  {contentSets.length === 1 && (
                    <span title={productVersionTitle(contentSets[0].productVersion)}>
                      {productVersionLabel(contentSets[0].productVersion)}
                    </span>
                  )}
                  {contentSets.length > 1 && (
                    <span title={contentSets.map(cs => productVersionLabel(cs.productVersion)).join(', ')}>
                      {contentSets.length} versions
                    </span>
                  )}
                </td>

                <td>
                  {summary === null ? (
                    <span className={styles.muted}>{EMPTY_CELL}</span>
                  ) : (
                    <span
                      className={`${styles.statusPill} ${styles[statusMetaFor(summary.status).className]}`}
                      title={summary.breakdown}
                    >
                      {statusMetaFor(summary.status).label}
                      {summary.count < summary.total && (
                        <span className={styles.statusFraction}>
                          {summary.count}/{summary.total}
                        </span>
                      )}
                    </span>
                  )}
                </td>

                <td className={styles.updated}>{formatDate(source.updatedAt)}</td>

                <td className={styles.actionsCol}>
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    aria-label={`${retry ? 'Retry deleting' : 'Delete'} ${source.name}`}
                    title={blocked
                      ? "Can't delete a source while one of its versions is still being processed"
                      : retry
                        ? "Deletion of this source hasn't finished — retry it"
                        : 'Delete this source and every version of it'}
                    disabled={blocked}
                    onClick={e => { e.stopPropagation(); onDelete?.(source) }}
                  >
                    <Icon name="trash" />
                  </button>
                </td>
              </tr>,

              expanded && (
                <tr key={`${source.id}-versions`} className={styles.subRow}>
                  <td colSpan={COLUMNS.length + 1}>
                    <ContentSetList
                      contentSets={contentSets}
                      onDelete={cs => onDeleteVersion?.(source, cs)}
                    />
                  </td>
                </tr>
              ),
            ]
          })}
        </tbody>
      </table>
    </div>
  )
}
